// Receipts for parts bought (BL-012): the ones waiting for the office, beside the bills, and the
// job's own on its sheet. Approving one confirms the vendor, the GST and how it was paid; on the
// supplier's account it becomes a vendor bill, on a company card it posts to the card. Voiding keeps
// it, with why.

import { useState } from 'react';
import { dayText, parseMoney } from './model.ts';
import {
  approveBlocker,
  approveDefaults,
  approveInput,
  cardsOf,
  overheadAccounts,
  receiptText,
  statusText,
  supplierText,
  totalOf,
  whereText,
  type ApproveForm,
  type JobPurchase,
} from './receipts-model.ts';
import type { Account, BankAccount } from './types.ts';
import { Empty, Loading, Money, Panel, useAct, useQuery, type Act } from './ui.tsx';

function ApproveReceipt({ p, act, onDone }: { p: JobPurchase; act: Act; onDone: () => void }) {
  const vendors = useQuery<{ vendors: { id: string; name: string; active: boolean }[] }>('vendors.list', {});
  const banks = useQuery<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {});
  const accounts = useQuery<{ accounts: Account[] }>('accounts.list', { includeInactive: false });
  const cards = cardsOf(banks.data?.bankAccounts ?? [], accounts.data?.accounts ?? []);
  const [f, setF] = useState<ApproveForm | null>(null);
  if (!vendors.data || !banks.data || !accounts.data)
    return <Loading error={vendors.error ?? banks.error ?? accounts.error} what="the approval" />;
  const form = f ?? approveDefaults(p, cards);
  const set = (patch: Partial<ApproveForm>) => setF({ ...form, ...patch });
  const blocker = approveBlocker(p, form);
  const gstText = form.gstCents === null ? '' : (form.gstCents / 100).toFixed(2);
  return (
    <div className="fgrid inline-form">
      <label>
        <span className="flabel">Paid</span>
        <select
          className="field"
          value={form.paidHow}
          onChange={(e) => set({ paidHow: e.target.value as ApproveForm['paidHow'] })}
        >
          <option value="on_account">On the supplier’s account</option>
          <option value="company_card" disabled={!cards.length}>
            With a company card
          </option>
        </select>
      </label>
      {form.paidHow === 'company_card' ? (
        <label>
          <span className="flabel">Card</span>
          <select
            className="field"
            value={form.bankAccountId}
            onChange={(e) => set({ bankAccountId: e.target.value })}
          >
            {cards.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.last4 ? ` ·${c.last4}` : ''}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label>
        <span className="flabel">Vendor</span>
        <select className="field" value={form.vendorId} onChange={(e) => set({ vendorId: e.target.value })}>
          <option value="">{form.paidHow === 'on_account' ? 'Pick the vendor' : 'None'}</option>
          {vendors.data.vendors
            .filter((v) => v.active)
            .map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
        </select>
      </label>
      {!p.jobId ? (
        <label>
          <span className="flabel">Account</span>
          <select
            className="field"
            value={form.accountId}
            onChange={(e) => set({ accountId: e.target.value })}
          >
            <option value="">Pick the account</option>
            {overheadAccounts(accounts.data.accounts).map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} {a.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label>
        <span className="flabel">GST on it</span>
        <input
          className="field"
          inputMode="decimal"
          value={gstText}
          placeholder="0.00"
          onChange={(e) => set({ gstCents: e.target.value.trim() ? (parseMoney(e.target.value) ?? 0) : 0 })}
        />
        <span className="muted small hint block">Claimed back, not cost. PST stays in the cost.</span>
      </label>
      <label>
        <span className="flabel">Receipt or invoice number</span>
        <input
          className="field"
          value={form.receiptRef}
          onChange={(e) => set({ receiptRef: e.target.value })}
        />
      </label>
      {blocker ? (
        <p className="muted small" role="note">
          {blocker}
        </p>
      ) : null}
      <div className="row-actions">
        <button
          className="btn primary"
          disabled={act.busy || !!blocker}
          onClick={() =>
            void (async () => {
              const ok = await act.run('jobPurchases.approve', approveInput(p, form), 'Approved and posted.');
              if (ok) onDone();
            })()
          }
        >
          Approve
        </button>
        <button className="btn quiet" onClick={onDone}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function VoidReceipt({ p, act, onDone }: { p: JobPurchase; act: Act; onDone: () => void }) {
  const [reason, setReason] = useState('');
  return (
    <div className="fgrid inline-form">
      <label>
        <span className="flabel">Why</span>
        <input
          className="field"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Recorded twice, the wrong job…"
        />
      </label>
      <div className="row-actions">
        <button
          className="btn"
          disabled={act.busy || !reason.trim()}
          onClick={() =>
            void (async () => {
              const ok = await act.run(
                'jobPurchases.void',
                { purchaseId: p.id, reason: reason.trim() },
                'Voided.',
              );
              if (ok) onDone();
            })()
          }
        >
          Void it
        </button>
        <button className="btn quiet" onClick={onDone}>
          Keep it
        </button>
      </div>
    </div>
  );
}

/** One receipt as a card: what, where, who, how much (to who may see it), its photo, and what can be done. */
export function ReceiptCard({ p, act, canApprove }: { p: JobPurchase; act: Act; canApprove: boolean }) {
  const [open, setOpen] = useState<'approve' | 'void' | null>(null);
  const total = totalOf(p);
  return (
    <li className="stone line-card">
      <div className="line-top">
        <div>
          <strong>
            {p.number} · {supplierText(p)}
            {p.kind === 'credit' ? ' (credit)' : ''}
          </strong>
          <span className="muted small">
            {whereText(p)}
            {p.staffName ? ` · ${p.staffName}` : ''} · {dayText(p.purchasedOn)}
            {p.receiptRef ? ` · ${p.receiptRef}` : ''}
          </span>
        </div>
        {total !== null ? <Money cents={p.kind === 'credit' ? -total : total} className="amount" /> : null}
      </div>
      <p className="small">
        <span className={`chip say${p.status === 'approved' ? ' ok' : p.status === 'void' ? ' bad' : ''}`}>
          {statusText(p)}
        </span>{' '}
        {p.receipt?.url ? (
          <a href={p.receipt.url} target="_blank" rel="noreferrer">
            {receiptText(p)}
          </a>
        ) : (
          <span className="muted">{receiptText(p)}</span>
        )}
        {p.note ? <span className="muted"> · {p.note}</span> : null}
        {p.voidReason ? <span className="muted"> · {p.voidReason}</span> : null}
      </p>
      {canApprove && p.status === 'submitted' && !open ? (
        <div className="row-actions">
          <button className="btn primary" disabled={act.busy} onClick={() => setOpen('approve')}>
            Approve…
          </button>
          <button className="btn quiet" disabled={act.busy} onClick={() => setOpen('void')}>
            Void…
          </button>
        </div>
      ) : null}
      {open === 'approve' ? <ApproveReceipt p={p} act={act} onDone={() => setOpen(null)} /> : null}
      {open === 'void' ? <VoidReceipt p={p} act={act} onDone={() => setOpen(null)} /> : null}
    </li>
  );
}

/** Beside the bills: every receipt waiting for the office. */
export function ReceiptsToApprove({
  toast,
  onChanged,
}: {
  toast: (m: string) => void;
  onChanged?: () => void;
}) {
  const q = useQuery<{ purchases: JobPurchase[] }>('jobPurchases.list', { status: 'submitted' });
  const act = useAct(toast, async () => {
    await q.reload();
    onChanged?.();
  });
  return (
    <Panel
      title="Receipts to approve"
      aside={q.data ? <span className="chip">{q.data.purchases.length}</span> : undefined}
    >
      {!q.data ? (
        <Loading error={q.error} what="the receipts" retry={() => void q.reload()} />
      ) : q.data.purchases.length === 0 ? (
        <Empty>No receipt is waiting for approval.</Empty>
      ) : (
        <ul className="cards">
          {q.data.purchases.map((p) => (
            <ReceiptCard key={p.id} p={p} act={act} canApprove />
          ))}
        </ul>
      )}
    </Panel>
  );
}
